import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { agriculturalAPI } from '../services/api';
import {
  Database,
  Download,
  Calendar,
  Tag,
  RefreshCw,
  AlertCircle,
  X,
  Search,
  CheckSquare,
  Square,
  Filter,
  Maximize2,
  Globe,
  Trophy,
  MapPin,
  BarChart3,
  TrendingUp,
} from 'lucide-react';
import { RootState } from '../store/store';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const INDEX_NAME = 'producer_prices';

const MapUpdater: React.FC<{ isExpanded: boolean }> = ({ isExpanded }) => {
  const map = useMap();
  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => {
        map.invalidateSize();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, map]);
  return null;
};

interface FilterValue {
  value: string;
  count: number;
}

interface VisualizationFilters {
  item?: string;
  element?: string | string[];
  area?: string | string[];
  fromYear?: string;
  toYear?: string;
  months?: string;
  aggregation?: 'average' | 'sum' | 'min' | 'max';
}

interface ChartDataPoint {
  name: string;
  value: number;
  [key: string]: any;
}

interface MapData {
  country: string;
  value: number;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

const ProducerPrices: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [filterValues, setFilterValues] = useState<{ [key: string]: FilterValue[] }>({});
  const [filters, setFilters] = useState<VisualizationFilters>({
    aggregation: 'average',
  });
  
  const [mapData, setMapData] = useState<MapData[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [elementTimeSeries, setElementTimeSeries] = useState<{ [element: string]: { [country: string]: ChartDataPoint[] } }>({});
  const [allResults, setAllResults] = useState<any[]>([]);
  const [globalResults, setGlobalResults] = useState<any[]>([]);
  const [worldGeoJSON, setWorldGeoJSON] = useState<any>(null);
  const [countryStats, setCountryStats] = useState<{ [country: string]: any }>({});
  const [showCountrySelector, setShowCountrySelector] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [elementSearchTerm, setElementSearchTerm] = useState('');
  const [expandedChart, setExpandedChart] = useState<{ type: 'element' | 'country', key: string, title: string, subtitle: string, chartData: any[], dataKeys: string[], colors: string[] } | null>(null);
  const [expandedMap, setExpandedMap] = useState(false);
  const [expandedTopProducers, setExpandedTopProducers] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load world countries GeoJSON
  useEffect(() => {
    const loadWorldGeoJSON = async () => {
      try {
        const response = await fetch('https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson');
        if (response.ok) {
          const data = await response.json();
          setWorldGeoJSON(data);
        }
      } catch (err) {
        console.error('Error loading GeoJSON:', err);
      }
    };
    loadWorldGeoJSON();
  }, []);

  // Load filter values on mount
  useEffect(() => {
    loadAllFilterValues();
  }, []);

  const loadAllFilterValues = async () => {
    try {
      const allFilterValues: { [key: string]: FilterValue[] } = {};
      
      // Load Item values
      try {
        const itemRes = await agriculturalAPI.getIndexTerms(INDEX_NAME, {
          field: 'item',
          size: 200,
        });
        if (itemRes.data.success && itemRes.data.data && Array.isArray(itemRes.data.data)) {
          const validItems = itemRes.data.data.filter((term: FilterValue) => 
            term.value && term.value.trim() !== ''
          );
          if (validItems.length > 0) {
            allFilterValues['Item'] = validItems;
            allFilterValues['item'] = validItems;
          }
        }
      } catch (e) {
        console.error('Failed to load item terms:', e);
      }

      // Load Element values
      try {
        const elementRes = await agriculturalAPI.getIndexTerms(INDEX_NAME, {
          field: 'element',
          size: 100,
        });
        if (elementRes.data.success && elementRes.data.data && Array.isArray(elementRes.data.data)) {
          const validElements = elementRes.data.data.filter((term: FilterValue) => 
            term.value && term.value.trim() !== ''
          );
          if (validElements.length > 0) {
            allFilterValues['Element'] = validElements;
            allFilterValues['element'] = validElements;
          }
        }
      } catch (e) {
        console.error('Failed to load element terms:', e);
      }

      // Load Area values
      try {
        const areaRes = await agriculturalAPI.getIndexTerms(INDEX_NAME, {
          field: 'area',
          size: 300,
        });
        if (areaRes.data.success && areaRes.data.data && Array.isArray(areaRes.data.data)) {
          const validAreas = areaRes.data.data.filter((term: FilterValue) => 
            term.value && term.value.trim() !== ''
          );
          if (validAreas.length > 0) {
            allFilterValues['Area'] = validAreas;
            allFilterValues['area'] = validAreas;
          }
        }
      } catch (e) {
        console.error('Failed to load area terms:', e);
      }

      // Load Months values
      try {
        const monthsRes = await agriculturalAPI.getIndexTerms(INDEX_NAME, {
          field: 'months',
          size: 50,
        });
        if (monthsRes.data.success && monthsRes.data.data && Array.isArray(monthsRes.data.data)) {
          const validMonths = monthsRes.data.data.filter((term: FilterValue) => 
            term.value && term.value.trim() !== ''
          );
          if (validMonths.length > 0) {
            allFilterValues['Months'] = validMonths;
            allFilterValues['months'] = validMonths;
          }
        }
      } catch (e) {
        console.error('Failed to load months terms:', e);
      }

      // Also load other filter values from getAllIndexFilterValues
      try {
        const response = await agriculturalAPI.getAllIndexFilterValues(INDEX_NAME);
        if (response.data.success) {
          for (const fieldName of Object.keys(response.data.data.filterValues)) {
            const values = response.data.data.filterValues[fieldName]?.values || [];
            const validValues = values.filter((v: FilterValue) => 
              v.value && v.value.trim() !== ''
            );
            if (validValues.length > 0) {
              allFilterValues[fieldName] = validValues;
            }
          }
        }
      } catch (err) {
        console.error('Failed to load other filter values:', err);
      }

      setFilterValues(allFilterValues);
    } catch (err) {
      console.error('Failed to load filter values:', err);
      setFilterValues({});
    }
  };

  // Auto-set default filters and visualize
  useEffect(() => {
    if (Object.keys(filterValues).length > 0 && mapData.length === 0 && !filters.item) {
      const defaultItem = filterValues['Item']?.[0]?.value || filterValues['item']?.[0]?.value;
      const defaultElement = filterValues['Element']?.[0]?.value || filterValues['element']?.[0]?.value;
      
      // Try to find Tunisia in the area list, otherwise use first available
      const areaList = filterValues['Area'] || filterValues['area'] || [];
      const tunisiaArea = areaList.find((area: FilterValue) => 
        area.value && area.value.toLowerCase().includes('tunisia')
      );
      const defaultArea = tunisiaArea?.value || areaList[0]?.value;
      
      if (defaultItem) {
        const newFilters: VisualizationFilters = {
          ...filters,
          aggregation: 'average',
          item: defaultItem,
        };
        if (defaultElement) {
          newFilters.element = defaultElement;
        }
        if (defaultArea) {
          newFilters.area = [defaultArea];
        }
        setFilters(newFilters);
      }
    }
  }, [filterValues]);

  // Auto-visualize when default filters are set
  useEffect(() => {
    if (filters.item && mapData.length === 0 && !loading) {
      setInitialLoading(true);
      const timer = setTimeout(() => {
        handleVisualize();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [filters.item]);

  const handleFilterChange = (fieldName: keyof VisualizationFilters, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleAreaChange = (selectedAreas: string[]) => {
    setFilters(prev => ({ ...prev, area: selectedAreas.length > 0 ? selectedAreas : undefined }));
  };

  const handleElementChange = (selectedElements: string[]) => {
    setFilters(prev => ({ ...prev, element: selectedElements.length > 0 ? selectedElements : undefined }));
  };

  const handleVisualize = async () => {
    if (!filters.item) {
      setError('Please select an Item');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: any = { limit: 10000 };
      
      if (filters.item) {
        params['item'] = filters.item;
      }
      if (filters.element) {
        if (Array.isArray(filters.element) && filters.element.length > 0) {
          params['element'] = filters.element;
        } else if (typeof filters.element === 'string' && filters.element.trim()) {
          params['element'] = filters.element;
        }
      }
      if (filters.area) {
        if (Array.isArray(filters.area) && filters.area.length > 0) {
          params['area'] = filters.area;
        } else if (typeof filters.area === 'string' && filters.area.trim()) {
          params['area'] = filters.area;
        }
      }
      if (filters.months) {
        params['months'] = filters.months;
      }
      if (filters.fromYear) {
        params['fromYear'] = filters.fromYear;
      }
      if (filters.toYear) {
        params['toYear'] = filters.toYear;
      }

      const response = await agriculturalAPI.searchIndex(INDEX_NAME, params);
      
      // Also fetch global data (without area filter)
      const selectedElements = Array.isArray(filters.element) ? filters.element : filters.element ? [filters.element] : [];
      const globalParams: any = { limit: 10000 };
      if (filters.item) {
        globalParams['item'] = filters.item;
      }
      if (selectedElements.length > 0) {
        globalParams['element'] = selectedElements;
      }
      if (filters.months) {
        globalParams['months'] = filters.months;
      }
      if (filters.fromYear) {
        globalParams['fromYear'] = filters.fromYear;
      }
      if (filters.toYear) {
        globalParams['toYear'] = filters.toYear;
      }
      
      let globalDataResults: any[] = [];
      try {
        const globalResponse = await agriculturalAPI.searchIndex(INDEX_NAME, globalParams);
        if (globalResponse.data.success) {
          globalDataResults = globalResponse.data.data;
          setGlobalResults(globalDataResults);
        }
      } catch (globalErr) {
        console.warn('Failed to fetch global data:', globalErr);
        setGlobalResults([]);
      }
      
      if (response.data.success) {
        const results = response.data.data;
        setAllResults(results);
        
        // Transform data for visualizations
        const transformedData = transformDataForVisualization(results, filters);
        setMapData(transformedData.mapData);
        
        if (filters.area) {
          const areas = Array.isArray(filters.area) ? filters.area : [filters.area];
          setSelectedCountries(areas);
          // Pass results directly to avoid state update timing issue
          updateTimeSeriesForCountries(areas, results);
        } else {
          setSelectedCountries([]);
          setElementTimeSeries({});
          setCountryStats({});
        }
      } else {
        setError(response.data.message || 'Visualization failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Visualization failed');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const transformDataForVisualization = (results: any[], filters: VisualizationFilters) => {
    if (results.length === 0) {
      return { mapData: [] };
    }

    const aggregationFunc = filters.aggregation || 'average';
    
    const aggregate = (values: number[]) => {
      if (values.length === 0) return 0;
      switch (aggregationFunc) {
        case 'sum': return values.reduce((a, b) => a + b, 0);
        case 'min': return Math.min(...values);
        case 'max': return Math.max(...values);
        case 'average':
        default: return values.reduce((a, b) => a + b, 0) / values.length;
      }
    };

    const byCountryElement: { [key: string]: { [element: string]: number[] } } = {};
    const selectedElements = Array.isArray(filters.element) ? filters.element : filters.element ? [filters.element] : [];
    const primaryElement = selectedElements.length > 0 ? selectedElements[0] : null;

    results.forEach(result => {
      const area = (result.area || result.Area || 'Unknown') as string;
      const value = parseFloat((result.value || result.Value || '0') as string);
      const element = (result.element || result.Element || '') as string;
      
      if (!area || area === 'Unknown' || isNaN(value) || value <= 0) {
        return;
      }
      
      if (filters.element) {
        const elements = Array.isArray(filters.element) ? filters.element : [filters.element];
        if (!elements.includes(element)) {
          return;
        }
      }

      if (!byCountryElement[area]) byCountryElement[area] = {};
      if (!byCountryElement[area][element]) byCountryElement[area][element] = [];
      byCountryElement[area][element].push(value);
    });

    const mapData: MapData[] = Object.keys(byCountryElement).map(country => {
      let valuesToUse: number[] = [];
      
      if (primaryElement && byCountryElement[country][primaryElement]) {
        valuesToUse = byCountryElement[country][primaryElement];
      } else {
        const firstElement = Object.keys(byCountryElement[country])[0];
        if (firstElement) {
          valuesToUse = byCountryElement[country][firstElement];
        }
      }
      
      return {
        country,
        value: valuesToUse.length > 0 ? aggregate(valuesToUse) : 0,
      };
    }).filter(d => d.value > 0);

    return { mapData };
  };

  const updateTimeSeriesForCountries = (countries: string[], resultsToUse?: any[]) => {
    if (countries.length === 0) {
      setElementTimeSeries({});
      setCountryStats({});
      return;
    }

    const aggregationFunc = filters.aggregation || 'average';
    const newElementTimeSeries: { [element: string]: { [country: string]: ChartDataPoint[] } } = {};
    const selectedElements = Array.isArray(filters.element) ? filters.element : filters.element ? [filters.element] : [];
    const fromYear = filters.fromYear ? parseInt(filters.fromYear) : null;
    const toYear = filters.toYear ? parseInt(filters.toYear) : null;

    // Use provided results or fall back to allResults from state
    const dataToUse = resultsToUse || allResults;

    countries.forEach(country => {
      const countryResults = dataToUse.filter(result => {
        const area = (result.area || result.Area || 'Unknown') as string;
        return area.toLowerCase().trim() === country.toLowerCase().trim();
      });
      
      const byElement: { [element: string]: { [year: string]: number[] } } = {};
      
      countryResults.forEach(result => {
        const element = (result.element || result.Element || '') as string;
        const year = (result.year || result.Year || '') as string;
        const value = parseFloat((result.value || result.Value || '0') as string);
        
        if (selectedElements.length > 0 && !selectedElements.includes(element)) {
          return;
        }
        
        if (year) {
          const yearNum = parseInt(year);
          if (fromYear && yearNum < fromYear) return;
          if (toYear && yearNum > toYear) return;
          
          if (!byElement[element]) byElement[element] = {};
          if (!byElement[element][year]) byElement[element][year] = [];
          byElement[element][year].push(value);
        }
      });

      const aggregate = (values: number[]) => {
        if (values.length === 0) return 0;
        switch (aggregationFunc) {
          case 'sum': return values.reduce((a, b) => a + b, 0);
          case 'min': return Math.min(...values);
          case 'max': return Math.max(...values);
          case 'average':
          default: return values.reduce((a, b) => a + b, 0) / values.length;
        }
      };

      Object.keys(byElement).forEach(element => {
        if (!newElementTimeSeries[element]) {
          newElementTimeSeries[element] = {};
        }
        
        const byYear = byElement[element];
        const sortedYears = Object.keys(byYear).sort((a, b) => parseInt(a) - parseInt(b));

        const timeSeries: ChartDataPoint[] = sortedYears.map(year => ({
          name: year,
          value: aggregate(byYear[year]),
        }));

        newElementTimeSeries[element][country] = timeSeries;
      });
    });

    setElementTimeSeries(newElementTimeSeries);
  };

  const getVisualizationTitle = () => {
    if (mapData.length === 0) return '';
    const item = filters.item || 'Item';
    const element = Array.isArray(filters.element) 
      ? filters.element.length > 0 
        ? filters.element.join(', ')
        : 'Producer Price'
      : filters.element || 'Producer Price';
    return `${element} of ${item} by country`;
  };

  const getVisualizationSubtitle = () => {
    if (mapData.length === 0) return '';
    const aggregation = filters.aggregation || 'average';
    const period = filters.fromYear && filters.toYear 
      ? `${filters.fromYear} - ${filters.toYear}`
      : 'All Years';
    return `${aggregation.charAt(0).toUpperCase() + aggregation.slice(1)} ${period}`;
  };

  const getColorForValue = (value: number, maxValue: number) => {
    if (maxValue === 0) return '#fef3c7';
    const ratio = value / maxValue;
    if (ratio <= 0.2) return '#fef3c7';
    if (ratio <= 0.4) return '#fde68a';
    if (ratio <= 0.6) return '#f59e0b';
    if (ratio <= 0.8) return '#ef4444';
    return '#dc2626';
  };

  const maxMapValue = mapData.length > 0 ? Math.max(...mapData.map(d => d.value)) : 0;

  const handleCountryClick = (country: string) => {
    setSelectedCountries(prev => {
      if (prev.includes(country)) {
        const newSelection = prev.filter(c => c !== country);
        updateTimeSeriesForCountries(newSelection);
        return newSelection;
      } else {
        const newSelection = [...prev, country];
        updateTimeSeriesForCountries(newSelection);
        return newSelection;
      }
    });
  };

  const toggleCountrySelection = (country: string) => {
    handleCountryClick(country);
  };

  const getFilteredCountries = () => {
    let filtered = mapData.map(d => d.country);
    if (countrySearchTerm.trim()) {
      const searchLower = countrySearchTerm.toLowerCase();
      filtered = filtered.filter(country => 
        country.toLowerCase().includes(searchLower)
      );
    }
    return filtered.sort();
  };

  const isCountryMatch = (area: string, country: string): boolean => {
    const areaNorm = area.toLowerCase().trim();
    const countryNorm = country.toLowerCase().trim();
    if (areaNorm === countryNorm) return true;
    if (areaNorm.includes(countryNorm) || countryNorm.includes(areaNorm)) return true;
    return false;
  };

  const findMatchingCountryName = (geoJsonName: string): string | null => {
    if (!geoJsonName || mapData.length === 0) return null;
    const normalized = geoJsonName.toLowerCase().trim();
    const directMatch = mapData.find(d => 
      d.country.toLowerCase().trim() === normalized
    );
    if (directMatch) return directMatch.country;
    return null;
  };

  const getCountryValue = (countryName: string): number | null => {
    if (!countryName || mapData.length === 0) return null;
    const normalized = countryName.toLowerCase().trim();
    const directMatch = mapData.find(d => 
      d.country.toLowerCase().trim() === normalized
    );
    if (directMatch) return directMatch.value;
    return null;
  };

  const getGeoJSONStyle = (feature: any) => {
    const geoJsonCountryName = feature.properties.NAME || feature.properties.name || '';
    const value = getCountryValue(geoJsonCountryName);
    const isSelected = selectedCountries.some(c => 
      isCountryMatch(c, geoJsonCountryName)
    );
    
    if (value !== null) {
      const color = getColorForValue(value, maxMapValue);
      return {
        fillColor: color,
        weight: isSelected ? 4 : 1.5,
        color: isSelected ? '#3b82f6' : '#333',
        fillOpacity: isSelected ? 0.9 : 0.7,
        opacity: 1,
      };
    }
    
    return {
      fillColor: '#e5e7eb',
      weight: 1,
      color: '#999',
      fillOpacity: 0.3,
      opacity: 0.5,
    };
  };

  const onEachCountry = (feature: any, layer: L.Layer) => {
    const geoJsonCountryName = feature.properties.NAME || feature.properties.name || '';
    const value = getCountryValue(geoJsonCountryName);
    const isSelected = selectedCountries.some(c => 
      isCountryMatch(c, geoJsonCountryName)
    );
    
    if (value !== null) {
      layer.on({
        click: () => {
          handleCountryClick(geoJsonCountryName);
        },
      });
      
      const popupContent = `
        <div style="min-width: 280px; text-align: left;">
          <div style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; padding: 12px; border-radius: 6px 6px 0 0; margin: -10px -10px 10px -10px;">
            <div style="font-weight: bold; font-size: 17px;">${geoJsonCountryName}</div>
            ${isSelected ? '<div style="font-size: 12px; background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; display: inline-block; margin-top: 4px;">✓ Selected</div>' : ''}
          </div>
          <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 12px; border-radius: 6px; margin-bottom: 10px;">
            <div style="font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 4px;">${filters.item || 'Item'}</div>
            <div style="font-size: 24px; font-weight: bold; color: #1e40af;">${value.toLocaleString()} <span style="font-size: 14px;">USD</span></div>
            <div style="font-size: 11px; color: #64748b; margin-top: 4px;">
              ${(filters.aggregation || 'average').charAt(0).toUpperCase() + (filters.aggregation || 'average').slice(1)} • ${filters.fromYear && filters.toYear ? `${filters.fromYear} - ${filters.toYear}` : 'All Years'}
            </div>
          </div>
          <div style="text-align: center; padding: 10px;">
            <div style="font-size: 11px; color: #64748b;">
              ${isSelected ? '✓ Selected for comparison' : 'Click to select for comparison'}
            </div>
          </div>
        </div>
      `;
      
      layer.bindPopup(popupContent);
    } else {
      layer.bindPopup(`
        <div style="min-width: 200px; text-align: center;">
          <div style="background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%); color: white; padding: 12px; border-radius: 6px 6px 0 0; margin: -10px -10px 10px -10px;">
            <div style="font-weight: bold; font-size: 16px;">${geoJsonCountryName}</div>
          </div>
          <div style="padding: 16px;">
            <div style="font-size: 48px; margin-bottom: 8px;">📭</div>
            <div style="color: #64748b; font-size: 13px;">No data available</div>
            <div style="color: #94a3b8; font-size: 11px; margin-top: 4px;">for selected filters</div>
          </div>
        </div>
      `);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-y-auto">
        <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h1 className="text-xl font-bold text-gray-900">Producer Prices</h1>
          <p className="text-xs text-gray-600 mt-1">Analyze producer prices data</p>
        </div>

        <div className="flex-1 p-4 space-y-4">
          {/* Item Filter */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Tag className="h-4 w-4 inline mr-1" />
              Item <span className="text-red-500">*</span>
            </label>
            {filterValues['Item'] || filterValues['item'] ? (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
                <select
                  value={filters.item || ''}
                  onChange={(e) => handleFilterChange('item', e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm appearance-none bg-white"
                >
                  <option value="">Select Item...</option>
                  {(filterValues['Item'] || filterValues['item'] || []).map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.value} {item.count ? `(${item.count})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <input
                type="text"
                value={filters.item || ''}
                onChange={(e) => handleFilterChange('item', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="Enter Item"
              />
            )}
          </div>

          {/* Element Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Database className="h-4 w-4 inline mr-1" />
              Element
            </label>
            {filterValues['Element'] || filterValues['element'] ? (
              <div className="space-y-2">
                {Array.isArray(filters.element) && filters.element.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 bg-indigo-50 rounded-md border border-indigo-200">
                    {filters.element.map((element) => (
                      <div key={element} className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded-md text-xs">
                        <span>{element}</span>
                        <button
                          onClick={() => {
                            const newElements = filters.element ? (Array.isArray(filters.element) ? filters.element : [filters.element]).filter((e: string) => e !== element) : [];
                            handleElementChange(newElements);
                          }}
                          className="ml-1 hover:bg-indigo-700 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={elementSearchTerm}
                    onChange={(e) => setElementSearchTerm(e.target.value)}
                    placeholder="Search elements..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
                <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white">
                  <div className="p-2 space-y-1">
                    {((filterValues['Element'] || filterValues['element'] || []).filter((element: FilterValue) => {
                      if (!elementSearchTerm.trim()) return true;
                      return element.value && element.value.toLowerCase().includes(elementSearchTerm.toLowerCase());
                    })).map((element: FilterValue) => {
                      const isSelected = Array.isArray(filters.element) 
                        ? filters.element.includes(element.value)
                        : filters.element === element.value;
                      return (
                        <div
                          key={element.value}
                          onClick={() => {
                            const currentElements = Array.isArray(filters.element) ? filters.element : filters.element ? [filters.element] : [];
                            if (isSelected) {
                              handleElementChange(currentElements.filter(e => e !== element.value));
                            } else {
                              handleElementChange([...currentElements, element.value]);
                            }
                          }}
                          className={`flex items-center gap-2 p-2 rounded-md cursor-pointer ${
                            isSelected ? 'bg-indigo-50 border-2 border-indigo-400' : 'hover:bg-gray-50 border-2 border-transparent'
                          }`}
                        >
                          {isSelected ? (
                            <CheckSquare className="h-5 w-5 text-indigo-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                          <span className={`text-sm flex-1 ${isSelected ? 'text-indigo-900 font-semibold' : 'text-gray-700'}`}>
                            {element.value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <input
                type="text"
                value={Array.isArray(filters.element) ? filters.element.join(', ') : (filters.element || '')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.includes(',')) {
                    const elements = value.split(',').map(e => e.trim()).filter(e => e);
                    handleElementChange(elements);
                  } else {
                    handleFilterChange('element', value);
                  }
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="Enter Element(s)"
              />
            )}
          </div>

          {/* Area Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Database className="h-4 w-4 inline mr-1" />
              Area
            </label>
            {filterValues['Area'] || filterValues['area'] ? (
              <div className="space-y-2">
                {Array.isArray(filters.area) && filters.area.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 bg-indigo-50 rounded-md border border-indigo-200">
                    {filters.area.map((area) => (
                      <div key={area} className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded-md text-xs">
                        <span>{area}</span>
                        <button
                          onClick={() => {
                            const newAreas = filters.area ? (Array.isArray(filters.area) ? filters.area : [filters.area]).filter((a: string) => a !== area) : [];
                            handleAreaChange(newAreas);
                          }}
                          className="ml-1 hover:bg-indigo-700 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    placeholder="Search areas..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
                <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white">
                  <div className="p-2 space-y-1">
                    {((filterValues['Area'] || filterValues['area'] || []).filter((area: FilterValue) => {
                      if (!countrySearchTerm.trim()) return true;
                      return area.value.toLowerCase().includes(countrySearchTerm.toLowerCase());
                    })).map((area: FilterValue) => {
                      const isSelected = Array.isArray(filters.area) 
                        ? filters.area.includes(area.value)
                        : filters.area === area.value;
                      return (
                        <div
                          key={area.value}
                          onClick={() => {
                            const currentAreas = Array.isArray(filters.area) ? filters.area : filters.area ? [filters.area] : [];
                            if (isSelected) {
                              handleAreaChange(currentAreas.filter(a => a !== area.value));
                            } else {
                              handleAreaChange([...currentAreas, area.value]);
                            }
                          }}
                          className={`flex items-center gap-2 p-2 rounded-md cursor-pointer ${
                            isSelected ? 'bg-indigo-50 border-2 border-indigo-400' : 'hover:bg-gray-50 border-2 border-transparent'
                          }`}
                        >
                          {isSelected ? (
                            <CheckSquare className="h-5 w-5 text-indigo-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                          <span className={`text-sm flex-1 ${isSelected ? 'text-indigo-900 font-semibold' : 'text-gray-700'}`}>
                            {area.value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <input
                type="text"
                value={Array.isArray(filters.area) ? filters.area.join(', ') : (filters.area || '')}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.includes(',')) {
                    const areas = value.split(',').map(a => a.trim()).filter(a => a);
                    handleAreaChange(areas);
                  } else {
                    handleFilterChange('area', value);
                  }
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="Enter Area(s)"
              />
            )}
          </div>

          {/* From Year */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="h-4 w-4 inline mr-1" />
              From Year
            </label>
            <input
              type="number"
              value={filters.fromYear || ''}
              onChange={(e) => handleFilterChange('fromYear', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              placeholder="e.g., 2000"
            />
          </div>

          {/* To Year */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="h-4 w-4 inline mr-1" />
              To Year
            </label>
            <input
              type="number"
              value={filters.toYear || ''}
              onChange={(e) => handleFilterChange('toYear', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              placeholder="e.g., 2024"
            />
          </div>

          {/* Months Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="h-4 w-4 inline mr-1" />
              Months
            </label>
            {filterValues['Months'] || filterValues['months'] ? (
              <select
                value={filters.months || ''}
                onChange={(e) => handleFilterChange('months', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">All Months</option>
                {(filterValues['Months'] || filterValues['months'] || []).map((month: FilterValue) => (
                  <option key={month.value} value={month.value}>
                    {month.value}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={filters.months || ''}
                onChange={(e) => handleFilterChange('months', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                placeholder="Enter Months"
              />
            )}
          </div>

          {/* Aggregation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aggregation
            </label>
            <select
              value={filters.aggregation || 'average'}
              onChange={(e) => handleFilterChange('aggregation', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              <option value="average">Average</option>
              <option value="sum">Sum</option>
              <option value="min">Minimum</option>
              <option value="max">Maximum</option>
            </select>
          </div>
        </div>

        {/* Visualize Button */}
        <div className="sticky bottom-0 bg-white pt-4 border-t border-gray-200 p-4">
          <button
            onClick={handleVisualize}
            disabled={loading || !filters.item}
            className="w-full bg-indigo-600 text-white px-6 py-2.5 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center font-medium"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Visualize'
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <span>{error}</span>
                </div>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {mapData.length > 0 && (() => {
            // Calculate statistics
            const aggregationFunc = filters.aggregation || 'average';
            const aggregate = (values: number[]) => {
              if (values.length === 0) return 0;
              switch (aggregationFunc) {
                case 'sum': return values.reduce((a, b) => a + b, 0);
                case 'min': return Math.min(...values);
                case 'max': return Math.max(...values);
                case 'average':
                default: return values.reduce((a, b) => a + b, 0) / values.length;
              }
            };

            // Calculate world total from global results
            const globalData = globalResults.length > 0 ? globalResults : allResults;
            const byCountry: { [key: string]: number[] } = {};
            globalData.forEach((result: any) => {
              const area = (result.area || result.Area || 'Unknown') as string;
              const value = parseFloat((result.value || result.Value || '0') as string);
              if (area && area !== 'Unknown' && !isNaN(value) && value > 0) {
                if (!byCountry[area]) byCountry[area] = [];
                byCountry[area].push(value);
              }
            });

            const topProducers = Object.keys(byCountry)
              .map(country => ({ country, value: aggregate(byCountry[country]) }))
              .filter(d => d.value > 0)
              .sort((a, b) => b.value - a.value);

            const totalWorldValue = topProducers.reduce((sum, p) => sum + p.value, 0);
            const topProducer = topProducers[0];
            const fromYear = filters.fromYear ? parseInt(filters.fromYear) : null;
            const toYear = filters.toYear ? parseInt(filters.toYear) : null;
            const yearsCount = fromYear && toYear ? toYear - fromYear + 1 : (allResults.length > 0 ? new Set(allResults.map((r: any) => r.year || r.Year)).size : 0);
            
            // Format values
            const formatValue = (val: number) => {
              if (val >= 1000000000) return `${(val / 1000000000).toFixed(1)}B`;
              if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
              if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
              return val.toFixed(1);
            };

            return (
              <div className="space-y-6">
                {/* Summary Cards with Colors and Icons */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* World Total Card */}
                  <div className="bg-white rounded-lg shadow-sm border-l-4 border-l-blue-500 relative overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-xs font-medium text-gray-600 mb-1">World Total</h3>
                          <p className="text-2xl font-bold text-gray-900">{formatValue(totalWorldValue)}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {filters.fromYear && filters.toYear ? `${filters.fromYear}-${filters.toYear}` : 'avg'}
                          </p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Globe className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex items-center text-green-600 text-xs mt-2">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        <span>+2.3%</span>
                      </div>
                    </div>
                  </div>

                  {/* Top Producer Card */}
                  <div className="bg-white rounded-lg shadow-sm border-l-4 border-l-orange-500 relative overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-xs font-medium text-gray-600 mb-1">Top Producer</h3>
                          <p className="text-sm font-semibold text-gray-900">{topProducer?.country || 'N/A'}</p>
                          <p className="text-lg font-bold text-gray-900 mt-1">
                            {topProducer ? formatValue(topProducer.value) : '0'}
                          </p>
                        </div>
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                          <Trophy className="h-6 w-6 text-orange-600" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Selected Countries Card */}
                  <div className="bg-white rounded-lg shadow-sm border-l-4 border-l-green-500 relative overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-xs font-medium text-gray-600 mb-1">Selected Countries</h3>
                          <p className="text-2xl font-bold text-gray-900">{selectedCountries.length}</p>
                          <p className="text-xs text-gray-500 mt-1">Click map to add more</p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <MapPin className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Data Points Card */}
                  <div className="bg-white rounded-lg shadow-sm border-l-4 border-l-blue-500 relative overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-xs font-medium text-gray-600 mb-1">Data Points</h3>
                          <p className="text-2xl font-bold text-gray-900">{yearsCount}</p>
                          <p className="text-xs text-gray-500 mt-1">years of data</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <BarChart3 className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map and Top Producers Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Global Production Distribution Card */}
                  {!expandedMap && !expandedChart && !expandedTopProducers && (
                    <div className="bg-white rounded-lg shadow-sm border-l-4 border-l-blue-500 overflow-hidden">
                      <div className="p-4 border-b border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <Globe className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <h3 className="text-sm font-bold text-gray-900">Global Production Distribution</h3>
                                <p className="text-xs text-gray-600">{getVisualizationTitle() || 'Producer Price by country (average)'}</p>
                              </div>
                            </div>
                          </div>
                          <button 
                            onClick={() => setExpandedMap(true)}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            <span>Click to expand</span>
                            <Maximize2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="h-[300px] relative cursor-pointer" onClick={() => setExpandedMap(true)}>
                        <MapContainer 
                          key="compact-map"
                          center={[20, 0]} 
                          zoom={2} 
                          style={{ height: '100%', width: '100%' }} 
                          scrollWheelZoom={false}
                        >
                          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                          {worldGeoJSON && mapData.length > 0 && (
                            <GeoJSON key={`geojson-compact-${mapData.length}`} data={worldGeoJSON} style={getGeoJSONStyle} onEachFeature={onEachCountry} />
                          )}
                        </MapContainer>
                      </div>
                    </div>
                  )}

                  {/* Top Production Producers Card */}
                  {!expandedMap && !expandedChart && !expandedTopProducers && topProducers.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border-l-4 border-l-blue-500 overflow-hidden">
                      <div className="p-4 border-b border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <Trophy className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <h3 className="text-sm font-bold text-gray-900">Top Production Producers</h3>
                                <p className="text-xs text-gray-600">
                                  {filters.fromYear && filters.toYear ? `${filters.fromYear} - ${filters.toYear}` : 'All Years'} average
                                </p>
                              </div>
                            </div>
                          </div>
                          <button 
                            onClick={() => setExpandedTopProducers(true)}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            <span>Click to expand</span>
                            <Maximize2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="p-4 max-h-[300px] overflow-y-auto">
                        <div className="space-y-3">
                          {/* World Total */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-700">1 World</span>
                            </div>
                            <div className="flex-1 mx-3">
                              <div className="h-6 bg-orange-500 rounded" style={{ width: '100%' }}></div>
                            </div>
                            <span className="text-sm font-bold text-gray-900">{formatValue(totalWorldValue)}</span>
                          </div>
                          
                          {/* Top Producers */}
                          {topProducers.slice(0, 7).map((producer, index) => {
                            const maxValue = topProducers[0]?.value || 1;
                            const widthPercent = (producer.value / maxValue) * 100;
                            const isOrange = index === 0;
                            return (
                              <div key={producer.country} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-700">{index + 2} {producer.country}</span>
                                </div>
                                <div className="flex-1 mx-3">
                                  <div 
                                    className={`h-6 rounded ${isOrange ? 'bg-orange-500' : 'bg-blue-500'}`}
                                    style={{ width: `${widthPercent}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{formatValue(producer.value)}</span>
                              </div>
                            );
                          })}
                          
                          {topProducers.length > 7 && (
                            <div className="text-xs text-gray-500 text-center pt-2">
                              +{topProducers.length - 7} more countries
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Element Charts */}
                {selectedCountries.length > 0 && Object.keys(elementTimeSeries).length > 0 && (
                  <div className="space-y-8">
                    {Object.keys(elementTimeSeries).map((element) => {
                      const elementData = elementTimeSeries[element];
                      const countriesWithData = selectedCountries.filter(country => 
                        elementData[country] && elementData[country].length > 0
                      );
                      
                      if (countriesWithData.length === 0) return null;
                      
                      const allYears = new Set<string>();
                      countriesWithData.forEach(country => {
                        elementData[country].forEach(point => {
                          allYears.add(point.name);
                        });
                      });
                      
                      const sortedYears = Array.from(allYears).sort((a, b) => parseInt(a) - parseInt(b));
                      
                      const chartData = sortedYears.map(year => {
                        const dataPoint: any = { name: year };
                        countriesWithData.forEach(country => {
                          const countryData = elementData[country].find(p => p.name === year);
                          dataPoint[country] = countryData ? countryData.value : null;
                        });
                        return dataPoint;
                      });
                      
                      return (
                        <div key={element} className="bg-white rounded-lg shadow-md p-3 border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h3 className="text-sm font-bold text-gray-900">{element}</h3>
                              <p className="text-xs text-gray-600">{filters.item} • {filters.fromYear || 'Start'} - {filters.toYear || 'End'}</p>
                            </div>
                          </div>
                          {chartData.length > 0 ? (
                            <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-3">
                              <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={chartData}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                                  <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                                  <YAxis stroke="#6b7280" fontSize={12} />
                                  <Tooltip />
                                  <Legend />
                                  {countriesWithData.map((country, countryIndex) => {
                                    const countryColor = COLORS[countryIndex % COLORS.length];
                                    return (
                                      <Area
                                        key={country}
                                        type="monotone"
                                        dataKey={country}
                                        name={country}
                                        stroke={countryColor}
                                        strokeWidth={3}
                                        fill={countryColor}
                                        fillOpacity={0.3}
                                      />
                                    );
                                  })}
                                </AreaChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="text-center text-gray-500 py-16">
                              <Database className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                              <p>No data available</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {mapData.length === 0 && initialLoading && (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <RefreshCw className="mx-auto h-12 w-12 text-blue-600 mb-4 animate-spin" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Loading data...</h3>
              <p className="text-gray-500">Setting up default filters and fetching data.</p>
            </div>
          )}
          {mapData.length === 0 && !loading && !initialLoading && (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Database className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No data to visualize</h3>
              <p className="text-gray-500">Select filters and click "Visualize" to see results.</p>
            </div>
          )}

          {/* Expanded Map Modal */}
          {expandedMap && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
              onClick={() => setExpandedMap(false)}
            >
              <div 
                className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Producer Price Distribution</h3>
                    <p className="text-sm text-gray-600 mt-1">{getVisualizationSubtitle()}</p>
                  </div>
                  <button
                    onClick={() => setExpandedMap(false)}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-6 w-6 text-gray-600" />
                  </button>
                </div>
                <div className="p-6">
                  <div className="h-[600px] rounded-lg overflow-hidden border-2 border-gray-300">
                    <MapContainer 
                      key="expanded-map-modal"
                      center={[20, 0]} 
                      zoom={2} 
                      style={{ height: '100%', width: '100%' }} 
                      scrollWheelZoom={true}
                    >
                      <MapUpdater isExpanded={expandedMap} />
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
                      {worldGeoJSON && mapData.length > 0 && (
                        <GeoJSON key={`geojson-${mapData.length}`} data={worldGeoJSON} style={getGeoJSONStyle} onEachFeature={onEachCountry} />
                      )}
                    </MapContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Expanded Top Producers Modal */}
          {expandedTopProducers && (() => {
            const aggregationFunc = filters.aggregation || 'average';
            const aggregate = (values: number[]) => {
              if (values.length === 0) return 0;
              switch (aggregationFunc) {
                case 'sum': return values.reduce((a, b) => a + b, 0);
                case 'min': return Math.min(...values);
                case 'max': return Math.max(...values);
                case 'average':
                default: return values.reduce((a, b) => a + b, 0) / values.length;
              }
            };
            const globalData = globalResults.length > 0 ? globalResults : allResults;
            const byCountry: { [key: string]: number[] } = {};
            globalData.forEach((result: any) => {
              const area = (result.area || result.Area || 'Unknown') as string;
              const value = parseFloat((result.value || result.Value || '0') as string);
              if (area && area !== 'Unknown' && !isNaN(value) && value > 0) {
                if (!byCountry[area]) byCountry[area] = [];
                byCountry[area].push(value);
              }
            });
            const topProducers = Object.keys(byCountry)
              .map(country => ({ country, value: aggregate(byCountry[country]) }))
              .filter(d => d.value > 0)
              .sort((a, b) => b.value - a.value);
            const totalWorldValue = topProducers.reduce((sum, p) => sum + p.value, 0);
            const formatValue = (val: number) => {
              if (val >= 1000000000) return `${(val / 1000000000).toFixed(1)}B`;
              if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
              if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
              return val.toFixed(1);
            };

            return (
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                onClick={() => setExpandedTopProducers(false)}
              >
                <div 
                  className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Top Production Producers</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {filters.fromYear && filters.toYear ? `${filters.fromYear} - ${filters.toYear}` : 'All Years'} average
                      </p>
                    </div>
                    <button
                      onClick={() => setExpandedTopProducers(false)}
                      className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <X className="h-6 w-6 text-gray-600" />
                    </button>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      {/* World Total */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-semibold text-gray-700">1 World</span>
                        </div>
                        <div className="flex-1 mx-4">
                          <div className="h-8 bg-orange-500 rounded" style={{ width: '100%' }}></div>
                        </div>
                        <span className="text-lg font-bold text-gray-900 min-w-[100px] text-right">{formatValue(totalWorldValue)}</span>
                      </div>
                      
                      {/* Top Producers */}
                      {topProducers.map((producer, index) => {
                        const maxValue = topProducers[0]?.value || 1;
                        const widthPercent = (producer.value / maxValue) * 100;
                        const isOrange = index === 0;
                        return (
                          <div key={producer.country} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-semibold text-gray-700">{index + 2} {producer.country}</span>
                            </div>
                            <div className="flex-1 mx-4">
                              <div 
                                className={`h-8 rounded ${isOrange ? 'bg-orange-500' : 'bg-blue-500'}`}
                                style={{ width: `${widthPercent}%` }}
                              ></div>
                            </div>
                            <span className="text-lg font-bold text-gray-900 min-w-[100px] text-right">{formatValue(producer.value)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default ProducerPrices;

